import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Button, CircularProgress, InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  HistoryEdu as HistoryIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  FilterAltOutlined as FilterIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { auditLogs } from '../BaseURL';

const PRIMARY      = '#1a6b8a';
const PRIMARY_BG   = 'rgba(26,107,138,0.08)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

// Friendly labels for the activity_type codes the backend writes.
const TYPE_LABEL = {
  LOGIN:                'Login',
  LOGIN_FAILED:         'Failed login',
  LOGIN_BLOCKED:        'Login blocked',
  PATIENT_REGISTER:     'Patient registered',
  VITALS_SAVE:          'Vitals recorded',
  NOTE_SAVE:            'Nursing note',
  ADMISSION:            'Admission',
  DISCHARGE:            'Discharge',
  READMISSION:          'Re-admission',
  PATIENT_DECEASED:     'Patient deceased',
  BED_ASSIGN:           'Bed assigned',
  BED_TRANSFER:         'Bed/ward transfer',
  MED_REQUEST:          'Medication prescribed',
  MED_SERVE:            'Medication served',
  STOCK_ADJUST:         'Stock adjustment',
  PRODUCT_DELETE:       'Product deleted',
  PAYMENT:              'Payment processed',
  REVERSAL_REQUEST:     'Reversal requested',
  REVERSAL_APPROVE:     'Reversal approved',
  REVERSAL_REJECT:      'Reversal rejected',
  LAB_RESULT:           'Lab result saved',
  SCAN_RESULT:          'Scan result saved',
  LAB_REQUEST:          'Lab requested',
  SERVICE_REQUEST:      'Service requested',
  STAFF_CREATE:         'Staff created',
  USER_DELETE:          'User deleted',
  USER_STATUS:          'User status changed',
  PASSWORD_CHANGE:      'Password changed',
  SETTING_UPDATE:       'Setting updated',
  COMPLIANCE_FOLLOWUP:  'Compliance follow-up',
};

const friendlyType = (t) => TYPE_LABEL[t] || (t || '—');

const tonePerType = (t) => {
  if (!t) return { bg: '#f1f5f9', fg: '#475569' };
  if (t.startsWith('LOGIN_FAIL') || t === 'LOGIN_BLOCKED') return { bg: 'rgba(220,38,38,0.10)', fg: '#b91c1c' };
  if (t === 'DISCHARGE' || t === 'PATIENT_DECEASED')       return { bg: 'rgba(234,179,8,0.10)', fg: '#a16207' };
  if (t === 'LOGIN')                                       return { bg: 'rgba(34,197,94,0.10)', fg: '#15803d' };
  if (t.startsWith('REVERSAL_REJECT') || t.endsWith('_DELETE')) return { bg: 'rgba(220,38,38,0.10)', fg: '#b91c1c' };
  if (t === 'PAYMENT' || t === 'REVERSAL_APPROVE')         return { bg: 'rgba(14,165,233,0.10)', fg: '#0369a1' };
  return { bg: PRIMARY_BG, fg: PRIMARY };
};

const AuditLog = () => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);   // [{username, count}]
  const [types, setTypes] = useState([]);   // distinct activity_type values
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [usernameFilter, setUsernameFilter] = useState('');
  const [typeFilter,     setTypeFilter]     = useState('');
  const [from,           setFrom]           = useState('');
  const [to,             setTo]             = useState('');
  const [search,         setSearch]         = useState('');
  const [searchInput,    setSearchInput]    = useState('');

  // Debounce the free-text search by 300 ms so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (usernameFilter) params.username = usernameFilter;
      if (typeFilter)     params.activityType = typeFilter;
      if (from)           params.from   = from;
      if (to)             params.to     = to;
      if (search)         params.search = search;

      const { data } = await axios.get(auditLogs, { params, timeout: 15000 });
      console.log('[audit log] GET /auditLogs →', data);
      if (data?.success) {
        setRows(data.results || []);
        setTotal(data.total || 0);
        setUsers(data.users || []);
        setTypes(data.types || []);
      } else {
        console.warn('[audit log] backend returned success=false', data);
        setRows([]);
      }
    } catch (err) {
      console.error('[audit log] load failed:', err?.response?.status, err?.response?.data || err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [usernameFilter, typeFilter, from, to, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const u = r.username || '— unknown —';
      if (!map.has(u)) map.set(u, []);
      map.get(u).push(r);
    }
    return Array.from(map.entries()).map(([username, entries]) => ({ username, entries }));
  }, [rows]);

  const clearFilters = () => {
    setUsernameFilter(''); setTypeFilter(''); setFrom(''); setTo('');
    setSearch(''); setSearchInput('');
  };

  const hasFilter = usernameFilter || typeFilter || from || to || search;

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <HistoryIcon sx={{ color: PRIMARY, fontSize: 18 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Administrative · Audit Trail
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            System Activity Log
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Every meaningful action performed across the system — grouped by the user who performed it.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Reload" arrow>
            <span>
              <IconButton size="small" onClick={fetchData} disabled={loading}
                sx={{ border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED }}>
                {loading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <FilterIcon sx={{ color: MUTED, fontSize: 18 }} />
          <Typography sx={{ ...LABEL_SX, color: MUTED }}>Filter</Typography>
          {hasFilter && (
            <Button
              onClick={clearFilters}
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
              sx={{ ml: 1, textTransform: 'none', color: MUTED, fontWeight: 600, fontSize: '0.74rem' }}
            >
              Clear all
            </Button>
          )}
        </Stack>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.25,
        }}>
          <Autocomplete
            size="small"
            value={usernameFilter || null}
            onChange={(_, v) => setUsernameFilter(v || '')}
            options={users.map(u => u.username)}
            getOptionLabel={(opt) => {
              const u = users.find(x => x.username === opt);
              return u ? `${opt} (${u.count})` : opt;
            }}
            renderInput={(p) => (
              <TextField
                {...p}
                placeholder="Any user"
                label="User"
                InputProps={{
                  ...p.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>
                      {p.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            size="small"
            label="Action type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value=""><em>All actions</em></MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>{friendlyType(t)}</MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to || undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><DateRangeIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from || undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><DateRangeIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>
              ),
            }}
          />

          <TextField
            size="small"
            placeholder="Search description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>
              ),
              endAdornment: searchInput ? (
                <IconButton size="small" onClick={() => setSearchInput('')}><ClearIcon fontSize="small" /></IconButton>
              ) : null,
            }}
          />
        </Box>
      </Paper>

      {/* Summary strip */}
      <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>
          Showing <strong style={{ color: STRONG }}>{rows.length}</strong> of <strong style={{ color: STRONG }}>{total}</strong> entries
        </Typography>
        {usernameFilter && (
          <Chip size="small" label={`User: ${usernameFilter}`} onDelete={() => setUsernameFilter('')}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: PRIMARY_BG, color: PRIMARY, border: `1px solid rgba(26,107,138,0.3)` }}
          />
        )}
        {typeFilter && (
          <Chip size="small" label={friendlyType(typeFilter)} onDelete={() => setTypeFilter('')}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: PRIMARY_BG, color: PRIMARY, border: `1px solid rgba(26,107,138,0.3)` }}
          />
        )}
        {(from || to) && (
          <Chip size="small"
            icon={<DateRangeIcon sx={{ fontSize: 14, color: PRIMARY + ' !important' }} />}
            label={`${from || '…'} → ${to || '…'}`}
            onDelete={() => { setFrom(''); setTo(''); }}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: PRIMARY_BG, color: PRIMARY, border: `1px solid rgba(26,107,138,0.3)` }}
          />
        )}
      </Box>

      {/* Grouped table */}
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}
      >
        {rows.length === 0 && !loading ? (
          <Box sx={{ p: 6, textAlign: 'center', color: MUTED }}>
            <HistoryIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
              No activity to show
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
              {hasFilter
                ? 'Try adjusting the filters above.'
                : 'No actions have been logged yet. Activity will appear here as users interact with the system.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 'calc(100vh - 360px)', overflow: 'auto' }}>
            {grouped.map(({ username, entries }) => (
              <Box key={username} sx={{ borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' } }}>
                {/* User group header */}
                <Box
                  sx={{
                    position: 'sticky', top: 0, zIndex: 1,
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1.1,
                    bgcolor: PRIMARY_BG,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16, color: PRIMARY }} />
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                    {username}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${entries.length} action${entries.length === 1 ? '' : 's'}`}
                    sx={{ ml: 0.5, height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#fff', color: PRIMARY, border: `1px solid rgba(26,107,138,0.3)` }}
                  />
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>When</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>Action</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>Description</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>Target</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>Status</TableCell>
                        <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1, borderBottom: `1px solid ${BORDER}` }}>From</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((r) => {
                        const tone = tonePerType(r.activity_type);
                        const ok = (r.status || '').startsWith('success') || !r.status;
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: MUTED, fontSize: '0.78rem', py: 0.85, borderBottom: `1px solid #f1f5f9`, whiteSpace: 'nowrap' }}>
                              {r.created_at}
                            </TableCell>
                            <TableCell sx={{ py: 0.85, borderBottom: `1px solid #f1f5f9` }}>
                              <Chip
                                size="small"
                                label={friendlyType(r.activity_type)}
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: tone.bg, color: tone.fg, border: `1px solid ${tone.fg}30` }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.85, borderBottom: `1px solid #f1f5f9`, color: STRONG, fontSize: '0.82rem', maxWidth: 440, whiteSpace: 'normal' }}>
                              {r.description || <span style={{ color: '#cbd5e1' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ py: 0.85, borderBottom: `1px solid #f1f5f9`, fontSize: '0.76rem', color: MUTED, whiteSpace: 'nowrap' }}>
                              {r.table_name
                                ? <>{r.table_name}{r.record_id ? <> · <strong style={{ color: STRONG }}>#{r.record_id}</strong></> : null}</>
                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ py: 0.85, borderBottom: `1px solid #f1f5f9` }}>
                              <Chip
                                size="small"
                                label={ok ? 'success' : (r.status || 'failed')}
                                sx={{
                                  height: 18, fontSize: '0.66rem', fontWeight: 700,
                                  bgcolor: ok ? 'rgba(34,197,94,0.10)' : 'rgba(220,38,38,0.10)',
                                  color:   ok ? '#15803d'              : '#b91c1c',
                                  border: `1px solid ${ok ? '#15803d40' : '#b91c1c40'}`,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.85, borderBottom: `1px solid #f1f5f9`, fontSize: '0.74rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                              {r.ip_address || <span style={{ color: '#cbd5e1' }}>—</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AuditLog;
